"use client";

// React & Three.js imports
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";

// Drei components
import {
  MeshTransmissionMaterial,
  useGLTF,
  AccumulativeShadows,
  RandomizedLight,
  Environment,
  OrbitControls,
  Center,
} from "@react-three/drei";

// Controls
import { useControls } from "leva";

// Custom components & hooks
import { PageTitle } from "@/components/rendering/PageTitle";
import { useSceneControls } from "@/hooks/useSceneControls";

// ===================== 类型定义 =====================
interface GelatinousCubeProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number | [number, number, number];
}

interface MaterialControlsConfig {
  meshPhysicalMaterial: boolean;
  transmissionSampler: boolean;
  backside: boolean;
  samples: number;
  resolution: number;
  transmission: number;
  roughness: number;
  thickness: number;
  ior: number;
  chromaticAberration: number;
  anisotropy: number;
  distortion: number;
  distortionScale: number;
  temporalDistortion: number;
  clearcoat: number;
  attenuationDistance: number;
  attenuationColor: string;
  color: string;
  bg: string;
}

// ===================== GelatinousCube 组件 =====================
function GelatinousCube(props: GelatinousCubeProps) {
  // 材质参数控制面板 - 支持MeshTransmissionMaterial与MeshPhysicalMaterial切换
  const config = useControls("Material Settings", {
    meshPhysicalMaterial: false,
    transmissionSampler: false,
    backside: false,
    samples: { value: 10, min: 1, max: 32, step: 1 },
    resolution: { value: 2048, min: 256, max: 2048, step: 256 },
    transmission: { value: 1, min: 0, max: 1 },
    roughness: { value: 0.0, min: 0, max: 1, step: 0.01 },
    thickness: { value: 3.5, min: 0, max: 10, step: 0.01 },
    ior: { value: 1.5, min: 1, max: 5, step: 0.01 },
    chromaticAberration: { value: 0.06, min: 0, max: 1 },
    anisotropy: { value: 0.1, min: 0, max: 1, step: 0.01 },
    distortion: { value: 0.0, min: 0, max: 1, step: 0.01 },
    distortionScale: { value: 0.3, min: 0.01, max: 1, step: 0.01 },
    temporalDistortion: { value: 0.5, min: 0, max: 1, step: 0.01 },
    clearcoat: { value: 1, min: 0, max: 1 },
    attenuationDistance: { value: 0.5, min: 0, max: 10, step: 0.01 },
    attenuationColor: "#ffffff",
    color: "#c9ffa1",
    bg: "#839681",
  }) as MaterialControlsConfig;

  const { nodes, materials } = useGLTF(
    "/assets/models/gelatinous_cube-transformed.glb",
  ) as any; // TODO: 添加模型类型定义

  return (
    <group dispose={null} {...props}>
      {/* 主要材质立方体 - 支持两种材质切换 */}
      <mesh geometry={nodes.cube1.geometry} position={[-0.56, 0.38, -0.11]}>
        {config.meshPhysicalMaterial ? (
          <meshPhysicalMaterial {...config} />
        ) : (
          <MeshTransmissionMaterial
            background={new THREE.Color(config.bg)}
            {...config}
          />
        )}
      </mesh>

      {/* 内部装饰元素 */}
      <mesh
        castShadow
        renderOrder={-100}
        geometry={nodes.cube2.geometry}
        material={materials.cube_mat}
        material-side={THREE.FrontSide}
        position={[-0.56, 0.38, -0.11]}
      />
      <mesh
        geometry={nodes.bubbles.geometry}
        material={materials.cube_bubbles_mat}
        position={[-0.56, 0.38, -0.11]}
      />

      {/* 内部武器和骸骨元素 */}
      <group position={[-0.56, 0.38, -0.41]}>
        <mesh
          geometry={nodes.arrows.geometry}
          material={materials.weapons_mat}
        />
        <mesh
          geometry={nodes.skeleton_1.geometry}
          material={materials.skele_mat}
        />
        <mesh
          geometry={nodes.skeleton_2.geometry}
          material={materials.weapons_mat}
          material-side={THREE.FrontSide}
        />
      </group>
    </group>
  );
}

// ===================== 场景控制配置类型 =====================
interface ShadowControlsConfig {
  shadowColor: string;
  shadowOpacity: number;
}

// ===================== 主页面组件 =====================
export default function TransmissionPage() {
  // 通用场景控制
  const sceneConfig = useSceneControls({
    defaultAutoRotate: true,
    defaultAutoRotateSpeed: 0.05,
    defaultCameraFov: 25,
    defaultAmbientLightIntensity: 1.0,
    autoRotateSpeedRange: [0, 1],
    cameraFovRange: [15, 75],
    ambientLightIntensityRange: [0, 3],
  });

  // 阴影控制面板
  const shadowConfig = useControls("Shadow Settings", {
    shadowColor: "#3ead5d",
    shadowOpacity: { value: 0.8, min: 0, max: 1, step: 0.1 },
  }) as ShadowControlsConfig;

  return (
    <div className="w-full h-full bg-background">
      {/* 页面标题 */}
      <PageTitle
        title="玻璃透射渲染"
        subtitle="MeshTransmissionMaterial"
        subtitleClassName="text-muted-foreground"
      />

      {/* 3D 渲染场景 */}
      <Canvas
        shadows
        camera={{
          position: [15, 0, 15],
          fov: sceneConfig.cameraFov,
        }}
      >
        {/* 光照系统 - 简洁环境光适合透射效果 */}
        <ambientLight intensity={sceneConfig.ambientLightIntensity * Math.PI} />

        {/* ================== 渲染对象 ================== */}
        <group position={[0, -2.5, 0]}>
          <Center top>
            {/* 主要展示：透射材质凝胶立方体 */}
            <GelatinousCube />
          </Center>

          {/* 阴影系统 */}
          <AccumulativeShadows
            temporal
            frames={100}
            alphaTest={0.9}
            color={shadowConfig.shadowColor}
            colorBlend={1}
            opacity={shadowConfig.shadowOpacity}
            scale={20}
          >
            <RandomizedLight
              radius={10}
              ambient={0.5}
              intensity={Math.PI}
              position={[2.5, 8, -2.5]}
              bias={0.001}
            />
          </AccumulativeShadows>
        </group>

        {/* ================== 场景系统 ================== */}

        {/* 相机控制 */}
        <OrbitControls
          minPolarAngle={0}
          maxPolarAngle={Math.PI / 2}
          autoRotate={sceneConfig.autoRotate}
          autoRotateSpeed={sceneConfig.autoRotateSpeed}
          makeDefault
        />

        {/* 环境贴图与背景 */}
        <Environment
          files="/assets/textures/dancing_hall_1k.hdr"
          background
          backgroundBlurriness={1}
        />
      </Canvas>
    </div>
  );
}
